import { categoryLabel } from './i18n/de';
import { DAMAGE_CATEGORIES, isDamageCategory, type DamageCategory } from './status';

/**
 * A damage has one or more types (owner, 17.10.2026: „Kratzer und
 * Lackschaden"). Stored as two columns since 0021: `category`, the first type
 * in catalogue order — what every client before 0021 sends and reads — and
 * `extra_categories`, the remaining ones comma-separated in catalogue order,
 * NULL for a single type. The database refuses any other spelling
 * (ck_damages_extra_categories lists every allowed set; categories.test.ts
 * compares that list with `categorySets()`).
 *
 * Pure, shared by API, web, app and the demo store.
 */

/** The given types, deduplicated and in catalogue order; null when empty or any is unknown. */
export function normalizeCategories(values: unknown): DamageCategory[] | null {
  if (!Array.isArray(values) || values.length === 0 || values.length > 20) return null;
  if (!values.every(isDamageCategory)) return null;
  const set = new Set<string>(values);
  return DAMAGE_CATEGORIES.filter((c) => set.has(c));
}

/** The two stored columns of a normalised set. */
export function categoryColumns(categories: readonly DamageCategory[]): {
  category: DamageCategory;
  extraCategories: string | null;
} {
  const [first, ...rest] = categories;
  if (first === undefined) throw new Error('a damage needs at least one category');
  return { category: first, extraCategories: rest.length ? rest.join(',') : null };
}

/** The full set from the two stored columns; tolerant of a missing or unknown extra. */
export function categoriesFromColumns(
  category: string,
  extraCategories: string | null | undefined,
): DamageCategory[] {
  const parts = [category, ...(extraCategories ? extraCategories.split(',') : [])];
  const known = parts.filter(isDamageCategory);
  const set = new Set<string>(known);
  const ordered = DAMAGE_CATEGORIES.filter((c) => set.has(c));
  return ordered.length ? ordered : isDamageCategory(category) ? [category] : [];
}

/** „Lackschaden, Kratzer“ — the labels in catalogue order, for running text and labels. */
export function categoriesText(categories: readonly DamageCategory[]): string {
  return DAMAGE_CATEGORIES.filter((c) => categories.includes(c))
    .map((c) => categoryLabel[c])
    .join(', ');
}

/**
 * Every allowed stored pair, as `category,extra` — the sets of two or more
 * types in catalogue order. The CHECK constraint of 0021 lists exactly these.
 */
export function categorySets(): string[] {
  const out: string[] = [];
  const n = DAMAGE_CATEGORIES.length;
  for (let mask = 1; mask < 1 << n; mask += 1) {
    const set = DAMAGE_CATEGORIES.filter((_, i) => mask & (1 << i));
    if (set.length >= 2) out.push(set.join(','));
  }
  return out.sort();
}
